import React, { useState } from 'react';
import { clsx } from 'clsx';
import { ChevronDown, type LucideIcon } from 'lucide-react';

interface Props {
  title: string;
  icon: LucideIcon;
  storageKey: string;
  theme?: 'light' | 'dark';
  staggerIndex?: number;
  defaultCollapsed?: boolean;
  badge?: { text: string; tone?: 'default' | 'red' };
  children: React.ReactNode;
}

// Reusable collapsible card matching the Novedades look — header (icon + title +
// optional badge + chevron) toggling a body, with the collapsed state persisted
// per storageKey so a user's layout choices stick across reloads.
export const CollapsibleSection: React.FC<Props> = ({
  title, icon: Icon, storageKey, theme = 'dark', staggerIndex = 0, defaultCollapsed = false, badge, children,
}) => {
  const isLight = theme !== 'dark';
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(storageKey);
      return v === null ? defaultCollapsed : v === '1';
    } catch { return defaultCollapsed; }
  });

  const toggle = () => setCollapsed(prev => {
    const next = !prev;
    try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch { /* ignore */ }
    return next;
  });

  return (
    <div
      className={clsx('stagger-item rounded-2xl border overflow-hidden mb-4', isLight ? 'bg-white border-slate-100 shadow-sm' : 'glass-panel border-white/5')}
      style={{ '--i': staggerIndex } as React.CSSProperties}
    >
      <button
        onClick={toggle}
        className={clsx('w-full flex items-center justify-between gap-3 px-5 py-4 transition-colors', isLight ? 'hover:bg-slate-50' : 'hover:bg-white/5')}
      >
        <span className="flex items-center gap-2.5 min-w-0">
          <span className={clsx('p-2 rounded-lg shrink-0', isLight ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-500/10 text-indigo-400')}>
            <Icon size={18} />
          </span>
          <span className={clsx('font-bold truncate', isLight ? 'text-slate-900' : 'text-white')}>{title}</span>
          {badge && (
            <span className={clsx(
              'text-[10px] font-black px-2 py-0.5 rounded-chip uppercase shrink-0',
              badge.tone === 'red' ? 'bg-rose-500/10 text-rose-500' : 'bg-indigo-500/10 text-indigo-500'
            )}>
              {badge.text}
            </span>
          )}
        </span>
        <ChevronDown size={20} className={clsx('text-slate-400 transition-transform shrink-0', collapsed ? '' : 'rotate-180')} />
      </button>
      {!collapsed && (
        <div className={clsx('px-5 pb-5 pt-4 border-t', isLight ? 'border-slate-50' : 'border-white/5')}>
          {children}
        </div>
      )}
    </div>
  );
};
